import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

export interface ValuePropCardData {
    /** Card title (displayed in all states) */
    title: string;
    /** Card description (displayed when card is active/playing) */
    description?: string;
    /** Optional link text (displayed when card is active/playing) */
    linkText?: string;
    /** Optional link URL */
    linkHref?: string;
    /** Image source URL. If both imageSrc and videoSrc are provided, imageSrc takes priority */
    imageSrc?: string;
    /** Video source URL (e.g. mp4). Used when imageSrc is not provided */
    videoSrc?: string;
    /** Alt text for the media (used as alt for images, aria-label for videos) */
    mediaAlt?: string;
}

interface ValuePropCardProps {
    card: ValuePropCardData;
    isActive: boolean;
    progress: number;
    onClick: () => void;
    index: number;
    dataEventProps?: DataEventProps;
}

const containerCss = css({
    display: "flex",
    flexDirection: "row",
    cursor: "pointer",
    textAlign: "left",
    width: "100%"
});

const progressBarContainerCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "4px",
    flexShrink: 0,
    borderRadius: "2px",
    backgroundColor: wacToken("color.neutral.light300"),
    overflow: "hidden",
    position: "relative"
});

const progressBarFillCss = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    width: "100%",
    backgroundColor: wacToken("color.primary.blue"),
    borderRadius: "2px",
    transition: "none"
});

const contentCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    paddingInlineStart: token("space.250"),
    flex: 1
});

const titleCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
    margin: "0 !important"
});

// Hardcoded because wacToken("color.neutral.light500") does not resolve correctly at runtime
const inactiveTitleCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, local/ensure-wac-token-usage
    color: "#8C8F97 !important"
});

const descriptionCss = css({
    margin: 0
});

const cardButtonCss = css({
    "display": "block",
    "width": "100%",
    "border": "none",
    "background": "none",
    "padding": 0,
    "textAlign": "left",
    "cursor": "pointer",

    "&:focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const linkContainerCss = css({
    paddingBlockStart: token("space.050")
});

export const ValuePropCard = ({
    card,
    isActive,
    progress,
    onClick,
    index,
    dataEventProps
}: ValuePropCardProps) => {
    return (
        <div
            css={containerCss}
            data-testid={`value-prop-card-${index}`}
            data-active={isActive}
        >
            {isActive && (
                <div css={progressBarContainerCss}>
                    <div
                        css={progressBarFillCss}
                        style={{ height: `${progress}%` }}
                    />
                </div>
            )}
            <div css={contentCss}>
                {!isActive ? (
                    <button type="button" css={cardButtonCss} onClick={onClick}>
                        <p
                            className="body-lg"
                            css={[titleCss, inactiveTitleCss]}
                        >
                            {card.title}
                        </p>
                    </button>
                ) : (
                    <>
                        <p className="card-title-sm" css={titleCss}>
                            {card.title}
                        </p>
                        {card.description && (
                            <p className="body-lg" css={descriptionCss}>
                                {card.description}
                            </p>
                        )}
                        {card.linkText && card.linkHref && (
                            <div css={linkContainerCss}>
                                <Link
                                    aria-label={card.linkText}
                                    text={card.linkText}
                                    href={card.linkHref}
                                    type="primary"
                                    dataEventProps={dataEventProps}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
